import Spinner from '@amzn/awsui-components-core/spinner';
import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { eLDAPGroup } from '../constants/AppConstants';
import { RequireLdapAccess } from './access/RequireLdapAccess';
import { DasFinSuiteApplicationBanner } from './AppBanner';
import { useAuth } from './context/AuthContextProvider';
import { AdminHomePage } from './das-finsuite/admin-components/AdminHomePage';
import { AdminAnnouncements } from './das-finsuite/admin-components/admin-announcements/AdminAnnouncements';
import ManageAnnouncements from './das-finsuite/admin-components/admin-announcements/AdminManageAnnouncements';
import { Applications } from './das-finsuite/admin-components/applications/Applications';
import { AdminNewApp } from './das-finsuite/admin-components/applications/admin-manage-application/AdminNewApp';
import { Feedbacks } from './das-finsuite/admin-components/feedback/Feedbacks';
import { LogoLibrary } from './das-finsuite/admin-components/logo-library/LogoLibrary';
import AllApps from './das-finsuite/all-apps/AllApps';
import Announcements from './das-finsuite/announcements/Announcements';
import { EmbedAppPage } from './das-finsuite/embedded-apps/EmbedAppPage';
import { SummerWellnessTrackerPage } from './das-finsuite/embedded-apps/SummerWellnessTrackerPage';
import {
  LazyCostMetricsMainPage as CostMetricsMainPage,
  LazyDogmaMain as DogmaMain,
  LazyPipelineOEDashboard as PipelineOEDashboard,
  LazyKingpinPage as KingpinPage,
  LazyPolicyEnginePage as PolicyEnginePage,
  LazyRiskRemediatorMain as RiskRemediatorMain,
  LazySasPageMain as SasPageMain,
  LazyTicketsPageMain as TicketsPageMain,
  LazyUpgradeMedicMain as UpgradeMedicMain,
  LazyUserMetricsPage as UserMetricsPage
} from './fintech-ops/shared/lazyRoutes';
import { ErrorFallback } from './generic-components/ErrorFallback';

import { AskQuickProvider } from './ask-quick/AskQuickContext';
import { AskQuickPanel } from './ask-quick/AskQuickPanel';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { FinanceCalendarManagement } from './das-finsuite/admin-components/finance-calendar-manage/FinanceCalendarManagement';
import { HomepageManagement } from './das-finsuite/admin-components/homepage-manage/HomePageManagement';
import { ViewMetadata } from './das-finsuite/admin-components/manage-metadata/ViewMetadata';
import { TeamPageManagement } from './das-finsuite/admin-components/teams-manage/TeamPageManagement';
import BaseballCardsAdminPage from './das-finsuite/baseball-cards/BaseballCardsAdminPage';
import { BaseballCardsHomePage } from './das-finsuite/baseball-cards/BaseballCardsHomePage';
import { EAHuddleHomePage as EAHuddleV2 } from './das-finsuite/ea-huddle-v2/EAHuddleHomePage';
import EAHuddleAdminPage from './das-finsuite/ea-huddle-v2/admin/EAHuddleAdminPage';
import KingpinGoalsAdminPage from './das-finsuite/kingpin/admin/KingpinGoalsAdminPage';
import { ConnectionsDashboard } from './das-finsuite/connections-scorecard/ConnectionsDashboard';
import { McpServersAdmin } from './das-finsuite/admin-components/mcp-servers/McpServersAdmin';
import { McpShowcasePage } from './das-finsuite/mcp-showcase/McpShowcasePage';
import { DFPTopNavigation } from './dfp-home-page/top-navigation/DFPTopNavigation';
import { BusinessReportingPage } from './fintech-ops/business-reporting/BusinessReportingPage';
import { FinTechOpsPage } from './fintech-ops/fintech-ops-dashboard/FinTechOpsDashboard';
import { KpiMetricsPage } from './fintech-ops/kpi-metrics/KpiMetricsPage';
import { MappingMainPage } from './fintech-ops/ops-mappings/MappingMainPage';

import LandingPage from './dfp-home-page/landing-page/LandingPage';
import { OEErrorFallback } from './fintech-ops/shared/OEErrorFallback';
import { OEProvidersWrapper } from './fintech-ops/shared/OEProvidersWrapper';
import { NotificationProvider } from './fintech-ops/shared/context/NotificationProvider';
import { UserMetricsProvider } from './fintech-ops/shared/context/UserMetricsProvider';
const SupportPage = React.lazy(() => import('./support/SupportPage'));
const TechMbrDocsPage = React.lazy(() => import('./das-finsuite/tech-mbr-docs/TechMbrDocsPage').then((m) => ({ default: m.TechMbrDocsPage })));
const WorkDocsCallbackHandler = React.lazy(() =>
  import('./das-finsuite/tech-mbr-docs/WorkDocsCallbackHandler').then((m) => ({ default: m.WorkDocsCallbackHandler }))
);

export const DasFinSuiteAdminWrapper = () => {
  const userAuthenticationDetails = useAuth();
  if (userAuthenticationDetails.isAdmin) {
    return <Outlet />;
  }
  return <Navigate to="/" replace />;
};

export const BaseballCardsWrapper = () => {
  const userAuthenticationDetails = useAuth();
  const hasAccess = [eLDAPGroup.BASEBALL_CARDS_LDAP, eLDAPGroup.DEV_LDAP].some((group) => userAuthenticationDetails.userLDAPGroups?.includes(group));
  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

export const EAHuddleAdminWrapper = () => {
  const userAuthenticationDetails = useAuth();
  if (userAuthenticationDetails.isHuddlePlusAdmin) {
    return <Outlet />;
  }
  return <Navigate to="/ea-huddle" replace />;
};

// Canary scorecard - powerusers (see all) or viewers (own subtree) in all stages;
// DEV_LDAP grants access ONLY in non-prod (dev/alpha) so developers can test in alpha
// without opening the route in beta/prod. Coarse route gate only; row-level scoping is
// enforced in the backend resolver.
export const ConnectionsScorecardWrapper = () => (
  <RequireLdapAccess
    anyOf={[eLDAPGroup.CANARY_SCORECARD_POWERUSERS_LDAP, eLDAPGroup.CANARY_SCORECARD_VIEWERS_LDAP]}
    nonProdOnly={[eLDAPGroup.DEV_LDAP]}
  />
);

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <ImpersonationProvider>
        <AskQuickProvider>
          <AskQuickPanel
            header={
              <div id="h">
                <DFPTopNavigation />
                <DasFinSuiteApplicationBanner />
              </div>
            }
          >
            <Suspense
              fallback={
                <div
                  style={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 1000
                  }}
                >
                  <Spinner size="large" />
                </div>
              }
            >
              <Routes>
                {/* LandingPage is accessible to all types of users */}
                <Route path="/" element={<LandingPage />} />

                {/* FinSuite Pages */}
                <Route path="/all-apps" element={<AllApps />} />
                <Route path="/all-apps/:id" element={<EmbedAppPage />} />
                {/* <Route path="/teams" element={<FinTechTeamHomePage />} /> */}
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/mcp" element={<McpShowcasePage />} />
                <Route path="/mcp/:serverId" element={<McpShowcasePage />} />
                {/* <Route path="/tutorials" element={<Tutorials />} /> */}
                <Route
                  path="/ea-huddle"
                  element={
                    <NotificationProvider>
                      <UserMetricsProvider>
                        <EAHuddleV2 />
                      </UserMetricsProvider>
                    </NotificationProvider>
                  }
                />

                {/* Org Tools - Wellness Tracker (embedded Harmony app) */}
                <Route path="/org-tools/wellness-tracker" element={<SummerWellnessTrackerPage />} />

                {/* Baseball Cards - accessible to BASEBALL_CARDS_LDAP and DEV_LDAP only */}
                <Route path="baseball-cards" element={<BaseballCardsWrapper />}>
                  <Route index element={<BaseballCardsHomePage />} />
                </Route>

                {/* WorkDocs OAuth callback handler (registered redirect URL) */}
                <Route
                  path="workdocs/callback"
                  element={
                    <WorkDocsCallbackHandler>
                      <></>
                    </WorkDocsCallbackHandler>
                  }
                />

                {/* MBR & Roadmap Docs */}
                <Route path="docs" element={<TechMbrDocsPage />} />

                {/* Canary (Connections intelligence) - gated to powerusers/viewers/DEV */}
                <Route path="canary" element={<ConnectionsScorecardWrapper />}>
                  <Route index element={<ConnectionsDashboard />} />
                </Route>
                {/* Back-compat: old /connections-scorecard links redirect to /canary */}
                <Route path="connections-scorecard" element={<Navigate to="/canary" replace />} />

                {/* Kingpin */}
                <Route path="kingpin">
                  <Route index element={<Navigate to="/kingpin/goals" replace />} />
                  <Route
                    path="*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <KingpinPage />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* FinTech Ops - OE routes with providers wrapper */}
                <Route
                  path="operational-excellence"
                  element={
                    <OEProvidersWrapper>
                      <ErrorBoundary FallbackComponent={OEErrorFallback} onReset={() => window.location.reload()}>
                        <Outlet />
                      </ErrorBoundary>
                    </OEProvidersWrapper>
                  }
                >
                  <Route
                    index
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <FinTechOpsPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="oe-insights"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <BusinessReportingPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="ticket-health/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <TicketsPageMain />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="user-metrics/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <UserMetricsPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="security-risks/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <SasPageMain />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="policy-engine/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <PolicyEnginePage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="pipeline-health/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <DogmaMain />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="risk-remediator/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <RiskRemediatorMain />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="upgrade-medic"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <UpgradeMedicMain />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="data-mapping"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <MappingMainPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="cost-metrics/*"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <CostMetricsMainPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="kpi-metrics"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <KpiMetricsPage />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="pipeline-oe-dashboard"
                    element={
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <PipelineOEDashboard />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Admin is accessible to admins only */}
                <Route path="admin" element={<DasFinSuiteAdminWrapper />}>
                  <Route index element={<AdminHomePage />} />
                  <Route path="/admin/applications" element={<Applications />} />
                  <Route path="/admin/manage-applications" element={<AdminNewApp />} />
                  <Route path="/admin/manage-applications/:id" element={<AdminNewApp />} />
                  <Route path="/admin/announcements" element={<AdminAnnouncements />}>
                    <Route path="/admin/announcements/:id" element={<ManageAnnouncements />} />
                  </Route>
                  <Route path="/admin/feedbacks" element={<Feedbacks />}></Route>
                  <Route path="/admin/logo-library" element={<LogoLibrary />}></Route>
                  <Route path="/admin/manage-homepage" element={<HomepageManagement />}></Route>
                  <Route path="/admin/finance-calendar" element={<FinanceCalendarManagement />}></Route>
                  <Route path="/admin/manage-team-page" element={<TeamPageManagement />}></Route>

                  <Route path="/admin/view-metadata" element={<ViewMetadata />}></Route>
                  <Route path="/admin/baseball-cards" element={<BaseballCardsAdminPage />} />
                  <Route path="/admin/kingpin-goals" element={<KingpinGoalsAdminPage />} />
                  <Route path="/admin/mcp-servers" element={<McpServersAdmin />} />
                  <Route path="/admin/manage-mcp-server" element={<div>TODO: MCP Server Form</div>} />
                  <Route path="/admin/manage-mcp-server/:id" element={<div>TODO: MCP Server Form</div>} />
                </Route>

                {/* Huddle+ admin - accessible to HUDDLEPLUS_ADMIN_LDAP only */}
                <Route path="/admin/ea-huddle" element={<EAHuddleAdminWrapper />}>
                  <Route index element={<EAHuddleAdminPage />} />
                </Route>
              </Routes>
            </Suspense>
          </AskQuickPanel>
        </AskQuickProvider>
      </ImpersonationProvider>
    </BrowserRouter>
  );
};
